// PrincipleTabs.tsx - Final version
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    ClipboardEdit,
    HelpCircleIcon,
    Lock,
    Save,
    ArrowRight,
    CheckCircle,
    ChevronLeft,
    Unlock,
    CircleHelp,
} from "lucide-react";
import { Formik, Form } from "formik";
import CustomAccordionCards from "../../../../apps/components/CustomAccordionCards";
import { MyEditor } from "../../../../apps/components/MyEditor";
import { EvaluationQuestion, Principle, PrincipleProgress, EvaluationResponse, UpdateEvaluationScoreQuestion, EvaluationDetails } from "../../core/_models";
import { completeProjectEvaluation, updateEvaluationScore } from "../../core/_request";
import { useSnackbar } from "../../../../../../_theme/context/SnackbarContext";
import { LogError } from "../../../../../../utils/GlobalLogging";
import { ReferenceDocumentModal, useReferenceDocumentModal } from "../../common/ReferenceDocumentModal";
import { ScoreSliderImagesModal, useScoreSliderImagesModal } from "../../common/ScoreSliderImagesModal";
import { ScoreHelp, ScoreHelpModal, useScoreHelpModal } from "../../common/ScoreHelpModal";
import ThemeTooltip from "../../../../apps/components/UI/ThemeTooltip";
import ConfirmationModal from "../../../../apps/components/ConfirmationModal";
import { isAbortedError } from "../../../../../../utils/global";
import { getApiErrorMessage } from "../../../../../../utils/utils";

// Type definitions
interface QuestionValue {
    points: number | null;
    comment: string;
    is_attempted: 'yes' | 'no';
}

interface FormValues {
    [key: string]: QuestionValue;
}

interface MergedQuestion extends EvaluationQuestion {
    response: EvaluationResponse | null;
}

interface SubPrincipleWithQuestions {
    pp_id: number;
    name: string;
    description: string;
    tip: string;
    weight: number;
    sequence: number;
    questions: MergedQuestion[];
}

interface PrincipleWithProgress extends Principle {
    totalQuestions: number;
    pendingQuestions: number;
    completedQuestions: number;
    subPrinciples: SubPrincipleWithQuestions[];
}

interface ScoreHelpData {
    [key: string]: string;
}

interface PrincipleTabsProps {
    evaluationPrinciples: Principle[];
    evaluationQuestions: EvaluationQuestion[];
    principle_progress: PrincipleProgress[];
    responses: EvaluationResponse[];
    allowEdit: boolean;
    evaluatorId: number;
    projectId: number;
    onRefreshData?: () => Promise<void>;
    scoringKeywords: { keyword: string, score: number, thinking: string }[];
    likertScaleLabels: { label: string, score: number }[];
    onUpdateEvaluationDetails: (data: EvaluationDetails) => void;
    readyToCompleteEvaluation: boolean;
}

const SCORE_IMAGES: Record<number, string> = {
    1: "/media/images/pet/score-slider-images/score_1.webp",
    3: "/media/images/pet/score-slider-images/score_3.webp",
    5: "/media/images/pet/score-slider-images/score_5.webp",
};

const getScoreImageUrl = (score: number): string | null => {
    return SCORE_IMAGES[score] || null;
};

const isScoreWithImage = (score: number): boolean => {
    return [1, 3, 5].includes(score);
};

const MAX_SCORE_POINTS = 5;

// Helper to check if score is valid (not null, undefined, or 0)
const isValidScore = (score: number | null | undefined): score is number => {
    return score !== null && score !== undefined && score > 0;
};

const getQuestionWeightedScore = (points: number, weight: number): number => {
    return Number(((points / MAX_SCORE_POINTS) * weight).toFixed(2));
};

export const PrincipleTabs = ({
    evaluationPrinciples,
    evaluationQuestions,
    principle_progress,
    responses,
    allowEdit = true,
    evaluatorId,
    projectId,
    scoringKeywords,
    likertScaleLabels,
    onRefreshData,
    onUpdateEvaluationDetails,
    readyToCompleteEvaluation
}: PrincipleTabsProps) => {
    const { showSnackbar } = useSnackbar();
    const [selectedTab, setSelectedTab] = useState<number | undefined>();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isCompleting, setIsCompleting] = useState<boolean>(false);
    const [showCompleteConfirmation, setShowCompleteConfirmation] = useState<boolean>(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string>("");
    const [scoreHelp, setScoreHelp] = useState<ScoreHelpData>({});

    const { isOpen, openModal, closeModal, principleId } = useReferenceDocumentModal();
    const { isOpen: isImageModalOpen, openModal: openImageModal, closeModal: closeImageModal } = useScoreSliderImagesModal();
    const { isOpen: isHelpModalOpen, openModal: openHelpModal, closeModal: closeHelpModal } = useScoreHelpModal();

    // Track if initial tab has been set
    const isInitialTabSetRef = useRef<boolean>(false);
    // Track which questions have been touched/interacted with
    const [touchedQuestions, setTouchedQuestions] = useState<Set<number>>(new Set());
    const [openSubPrincipleId, setOpenSubPrincipleId] = useState<number | null>(null);
    const [ratingErrorQuestionIds, setRatingErrorQuestionIds] = useState<number[]>([]);

    // Create maps for quick lookups
    const progressMap = useMemo((): Map<number, PrincipleProgress> => {
        const map = new Map<number, PrincipleProgress>();
        principle_progress?.forEach((p) => map.set(p.pp_id, p));
        return map;
    }, [principle_progress]);

    const responseMap = useMemo((): Map<number, EvaluationResponse> => {
        const map = new Map<number, EvaluationResponse>();
        responses?.forEach((r) => map.set(r.question_id, r));
        return map;
    }, [responses]);

    // Merge questions with sub-principles and add response data
    const mergeQuestionsWithSubPrinciples = useMemo((): PrincipleWithProgress[] => {
        if (!evaluationQuestions || !evaluationPrinciples) return [];

        const questionsMap = new Map<number, EvaluationQuestion[]>();
        evaluationQuestions.forEach((q) => {
            if (!questionsMap.has(q.pp_id)) {
                questionsMap.set(q.pp_id, []);
            }
            questionsMap.get(q.pp_id)?.push(q);
        });

        return evaluationPrinciples.map((principle): PrincipleWithProgress => {
            const progress = progressMap.get(principle.pp_id);
            return {
                ...principle,
                totalQuestions: progress?.total || 0,
                pendingQuestions: progress?.pending || 0,
                completedQuestions: (progress?.total || 0) - (progress?.pending || 0),
                subPrinciples: principle.sub_principles.map((sub): SubPrincipleWithQuestions => ({
                    ...sub,
                    questions: (questionsMap.get(sub.pp_id) || []).map((q): MergedQuestion => ({
                        ...q,
                        response: responseMap.get(q.question_id) || null,
                    })),
                })),
            };
        });
    }, [evaluationPrinciples, evaluationQuestions, progressMap, responseMap]);

    const principleTabs = useMemo(() => {
        return [...mergeQuestionsWithSubPrinciples]
            .sort((a, b) => a.sequence - b.sequence)
            .map((principle) => ({
                id: principle.pp_id.toString(),
                label: principle.name,
                icon: (
                    <img
                        src={principle.image}
                        alt={principle.name}
                        width={20}
                        height={20}
                        style={{ objectFit: "contain" }}
                    />
                ),
                description: principle.description,
                count: principle.subPrinciples.length,
                progress: {
                    total: principle.totalQuestions,
                    completed: principle.completedQuestions,
                    pending: principle.pendingQuestions,
                },
                subPrinciples: [...principle.subPrinciples].sort(
                    (a, b) => a.sequence - b.sequence
                ),
                pp_id: principle.pp_id,
            }));
    }, [mergeQuestionsWithSubPrinciples]);

    const currentTabIndex = useMemo((): number => {
        return principleTabs.findIndex(
            (t) => Number(t.id) === selectedTab
        );
    }, [principleTabs, selectedTab]);

    // Get all questions from all sub-principles for form
    const allQuestions = useMemo((): MergedQuestion[] => {
        const questions: MergedQuestion[] = [];
        principleTabs.forEach((tab) => {
            tab.subPrinciples.forEach((sub) => {
                sub.questions.forEach((q) => {
                    questions.push(q);
                });
            });
        });
        return questions;
    }, [principleTabs]);

    // Initial form values - treat 0 as invalid and set to null
    const initialValues = useMemo((): FormValues => {
        const values: FormValues = {};
        allQuestions.forEach((q) => {
            const responsePoints = q.response?.points;
            // Only use response points if it's valid (> 0)
            const hasValidResponse = isValidScore(responsePoints);
            values[`question_${q.question_id}`] = {
                points: hasValidResponse ? responsePoints : null,
                comment: q.response?.comment ?? "",
                is_attempted: hasValidResponse ? 'yes' : 'no',
            };
        });
        return values;
    }, [allQuestions]);

    const goToPreviousTab = useCallback((): void => {
        const prevIndex = currentTabIndex - 1;
        if (prevIndex >= 0) {
            const prevTab = principleTabs[prevIndex];
            setSelectedTab(Number(prevTab.id));
        }
    }, [currentTabIndex, principleTabs]);

    const handleTabChange = useCallback((tabId: number): void => {
        setSelectedTab(tabId);
        setRatingErrorQuestionIds([]);
    }, []);

    const principleTabsRef = useRef(principleTabs);
    principleTabsRef.current = principleTabs;

    useEffect(() => {
        const activeTab = principleTabsRef.current.find((tab) => Number(tab.id) === selectedTab);
        setOpenSubPrincipleId(activeTab?.subPrinciples[0]?.pp_id ?? null);
        setRatingErrorQuestionIds([]);
    }, [selectedTab]);

    // Mark question as touched when user interacts with it
    const markQuestionTouched = useCallback((questionId: number) => {
        setTouchedQuestions(prev => new Set(prev).add(questionId));
    }, []);

    const hasFormChanges = useCallback((values: FormValues): boolean => {
        return allQuestions.some((question) => {
            const fieldName = `question_${question.question_id}`;
            const current = values[fieldName];
            const initial = initialValues[fieldName];

            const currentPoints = isValidScore(current?.points) ? current.points : null;
            const initialPoints = isValidScore(initial?.points) ? initial.points : null;
            const currentComment = (current?.comment ?? '').trim();
            const initialComment = (initial?.comment ?? '').trim();

            return currentPoints !== initialPoints || currentComment !== initialComment;
        });
    }, [allQuestions, initialValues]);

    // Save handlers with useCallback
    const handleSave = useCallback(async (values: FormValues): Promise<boolean> => {
        if (!allowEdit) return false;

        if (!hasFormChanges(values)) {
            showSnackbar('There are no changes to update.', 'warning');
            return false;
        }

        try {
            setIsSaving(true);

            const saveData: UpdateEvaluationScoreQuestion[] = allQuestions.map((q) => {
                const fieldName = `question_${q.question_id}`;
                const value = values[fieldName];
                const points = value?.points;

                // Check if there's a valid response (points > 0)
                const hasValidResponse = isValidScore(q.response?.points);
                const isTouched = touchedQuestions.has(q.question_id);
                const hasValidValue = isValidScore(points);

                // Determine if the question should be considered attempted
                const isAttempted = hasValidValue || hasValidResponse || isTouched;

                // Determine final points
                let finalPoints: number;
                if (hasValidValue) {
                    // Use the valid value from form
                    finalPoints = points as number;
                } else if (hasValidResponse) {
                    // Use the existing valid response
                    finalPoints = q.response?.points as number;
                } else if (isTouched) {
                    // If touched but no valid value, default to 1
                    finalPoints = 1;
                } else {
                    // If not touched and no valid response, still default to 1 but mark as not attempted
                    finalPoints = 1;
                }

                return {
                    question_id: q.question_id,
                    points: finalPoints,
                    comment: value?.comment ?? "",
                    is_attempted: isAttempted ? 'yes' : 'no',
                };
            });

            const response = await updateEvaluationScore({
                project_id: projectId,
                evaluator_user_id: evaluatorId,
                questions: saveData,
            });

            if (response.status === 'ok') {
                showSnackbar(response.message || 'Evaluation saved successfully!', 'success');

                onUpdateEvaluationDetails(response.data);
                return true;
            } else {
                showSnackbar(response.message || 'Failed to save evaluation', 'danger');
                return false;
            }
        } catch (error) {
            LogError("Failed to save evaluation.", error, "updateEvaluationScore");
            showSnackbar(getApiErrorMessage(error, 'Failed to save evaluation. Please try again.'), 'danger');
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [allowEdit, allQuestions, projectId, evaluatorId, principleTabs, selectedTab, showSnackbar, touchedQuestions, hasFormChanges]);

    const handleCompleteEvaluation = useCallback(async () => {
        try {
            setIsCompleting(true);
            const response = await completeProjectEvaluation(projectId);
            if (response.status === 'ok') {
                showSnackbar(response.message || 'Evaluation completed successfully!', 'success');
                onRefreshData?.();
                handleCloseCompleteConfirmation();
            } else {
                showSnackbar(response.message || 'Failed to complete evaluation', 'danger');
            }
        } catch (error: unknown) {
            const err = error as Error;
            if (isAbortedError(err)) {
            }
            LogError("Failed to complete evaluation.", error, "completeProjectEvaluation");
            showSnackbar(getApiErrorMessage(err, 'Failed to complete evaluation. Please try again.'), 'danger');
        } finally {
            setIsCompleting(false);
        }
    }, [projectId, showSnackbar]);

    const getUnratedSubPrinciples = useCallback((values: FormValues): { pp_id: number; questionIds: number[] }[] => {
        const activeTab = principleTabs.find((tab) => Number(tab.id) === selectedTab);

        if (!activeTab) {
            return [];
        }

        return activeTab.subPrinciples
            .map((sub) => ({
                pp_id: sub.pp_id,
                questionIds: sub.questions
                    .filter((question) => !isValidScore(values[`question_${question.question_id}`]?.points))
                    .map((question) => question.question_id),
            }))
            .filter((sub) => sub.questionIds.length > 0);
    }, [principleTabs, selectedTab]);

    const validateActivePrincipleRatings = useCallback((values: FormValues): boolean => {
        const unratedSubPrinciples = getUnratedSubPrinciples(values);

        if (unratedSubPrinciples.length === 0) {
            setRatingErrorQuestionIds([]);
            return true;
        }

        const firstIncompleteSub = unratedSubPrinciples[0];
        setOpenSubPrincipleId(firstIncompleteSub.pp_id);
        setRatingErrorQuestionIds(firstIncompleteSub.questionIds);

        window.setTimeout(() => {
            const firstQuestionId = firstIncompleteSub.questionIds[0];
            document
                .getElementById(`question-rating-${firstQuestionId}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);

        return false;
    }, [getUnratedSubPrinciples]);

    const handleSaveAndNext = useCallback(async (values: FormValues): Promise<void> => {
        if (!validateActivePrincipleRatings(values)) {
            return;
        }

        const goToNextTab = (): void => {
            const nextIndex = currentTabIndex + 1;

            if (nextIndex < principleTabs.length) {
                const nextTab = principleTabs[nextIndex];
                setSelectedTab(Number(nextTab.id));
            }
        };

        if (!hasFormChanges(values)) {
            goToNextTab();
            return;
        }

        const success = await handleSave(values);

        if (success) {
            goToNextTab();
        }
    }, [validateActivePrincipleRatings, handleSave, currentTabIndex, principleTabs, hasFormChanges]);

    const handleOpenCompleteConfirmation = useCallback((): void => {
        setShowCompleteConfirmation(true);
    }, []);

    const handleCloseCompleteConfirmation = useCallback((): void => {
        setShowCompleteConfirmation(false);
    }, []);

    // Helper functions with useCallback
    const getDisplayScore = useCallback((fieldValue: QuestionValue | undefined, currentScore: number): number => {
        // If field has a valid value (> 0), use it
        if (fieldValue?.points !== undefined && fieldValue?.points !== null && fieldValue.points > 0) {
            return fieldValue.points;
        }
        // Otherwise use the current score from response (if valid)
        if (currentScore > 0) {
            return currentScore;
        }
        // Default to 1
        return 1;
    }, []);

    const calculateWeightedScore = useCallback((questions: MergedQuestion[], values: FormValues): number => {
        if (!questions || questions.length === 0) return 0;

        return questions.reduce((total, question) => {
            const fieldValue = values[`question_${question.question_id}`];
            const points = isValidScore(fieldValue?.points)
                ? fieldValue.points
                : (isValidScore(question.response?.points) ? question.response.points : 0);

            if (points <= 0) {
                return total;
            }

            return total + getQuestionWeightedScore(points, question.weight);
        }, 0);
    }, []);

    // Set initial tab - only once
    useEffect(() => {
        if (!isInitialTabSetRef.current && evaluationPrinciples?.length > 0) {
            const principle = [...evaluationPrinciples]
                .sort((a, b) => a.sequence - b.sequence)[0]?.pp_id;
            if (principle) {
                setSelectedTab(principle);
                isInitialTabSetRef.current = true;
            }
        }
    }, [evaluationPrinciples]);

    const minMaxScore = useMemo((): { min: number; max: number } => {
        if (!likertScaleLabels?.length) {
            return { min: 1, max: 5 };
        }

        const scores = likertScaleLabels.map((item) => item.score);

        return {
            max: Math.max(...scores),
            min: Math.min(...scores),
        };
    }, [likertScaleLabels]);

    const marks = useMemo((): number[] => {
        return Array.from(
            { length: Math.floor((minMaxScore.max - minMaxScore.min) / 0.5) + 1 },
            (_, index) => Number((minMaxScore.min + index * 0.5).toFixed(2))
        );
    }, [minMaxScore]);

    // Generate accordion items with useCallback
    const generateAccordionItems = useCallback((
        subPrinciples: SubPrincipleWithQuestions[],
        formikHelpers: {
            values: FormValues;
            setFieldValue: (field: string, value: QuestionValue) => void;
        }
    ) => {
        const { values, setFieldValue } = formikHelpers;

        const handleScoreChange = (
            fieldName: string,
            questionId: number,
            e: React.ChangeEvent<HTMLInputElement>
        ): void => {
            const newPoints = parseFloat(e.target.value);

            // Mark question as touched
            markQuestionTouched(questionId);
            setRatingErrorQuestionIds((prev) => prev.filter((id) => id !== questionId));

            setFieldValue(fieldName, {
                ...values[fieldName],
                points: newPoints,
                is_attempted: 'yes',
            });

            if (isScoreWithImage(newPoints)) {
                const imageUrl = getScoreImageUrl(newPoints);

                if (imageUrl) {
                    setCurrentImageUrl(imageUrl);
                    requestAnimationFrame(() => {
                        openImageModal();
                    });
                }
            }
        };

        const handleOpenScoreHelpModal = (scoreHelpData: ScoreHelp): void => {
            setScoreHelp(scoreHelpData);
            openHelpModal();
        };

        const handleCommentChange = (fieldName: string, questionId: number, value: string): void => {
            // Mark question as touched when comment is added
            if (value && value.trim().length > 0) {
                markQuestionTouched(questionId);
            }

            const currentPoints = values[fieldName]?.points;
            setFieldValue(fieldName, {
                ...values[fieldName],
                comment: value,
                is_attempted: isValidScore(currentPoints) ? 'yes' : 'no',
            });
        };

        return subPrinciples.map((sub) => {
            const calculatedWeightedScore = calculateWeightedScore(sub.questions, values);

            const hasRatingError = sub.questions.some((question) =>
                ratingErrorQuestionIds.includes(question.question_id)
            );

            return {
                id: sub.pp_id,
                className: "mb-2",
                isError: hasRatingError,
                title: <div className="d-flex align-items-center gap-4"><p className="fw-bold mb-0">{sub.name}</p> <ThemeTooltip text={sub.tip} /></div>,
                subtitle: <div className="px-3 py-2 text-info bg-light-info rounded border border-info mt-2">{sub.description}</div>,
                rightContent: (
                    <div className="d-flex align-items-center gap-5 mx-5">
                        <div className="text-center">
                            <div className="fs-8 fw-semibold text-gray-700 text-nowrap">Weight</div>
                            <div className="fw-bold fs-6 bg-info bg-opacity-20 rounded py-1 px-2 mt-1">{sub.weight.toFixed(2)}</div>
                        </div>
                        <div className="d-flex flex-column align-items-center">
                            <div className="fs-8 fw-semibold text-gray-700 text-nowrap">Weighted Score</div>
                            <div className="fw-bold fs-6 bg-warning bg-opacity-20 rounded py-1 px-2 mt-1">{calculatedWeightedScore.toFixed(2)}</div>
                        </div>
                    </div>
                ),
                content: (
                    <div className="w-100">
                        {sub.questions && sub.questions.length > 0 ? (
                            <div className="d-flex flex-column gap-4">
                                {sub.questions
                                    .sort((a, b) => a.sequence - b.sequence)
                                    .map((question) => {
                                        const response = question.response;
                                        const currentScore = isValidScore(response?.points) ? response.points : 1;
                                        const isDisabled = !allowEdit;
                                        const fieldName = `question_${question.question_id}`;
                                        const fieldValue = values[fieldName];
                                        const displayScore = getDisplayScore(fieldValue, currentScore as number);
                                        const isUnanswered = !isValidScore(fieldValue?.points);
                                        const showRatingError = ratingErrorQuestionIds.includes(question.question_id);

                                        return (
                                            <div
                                                key={question.question_id}
                                                id={`question-rating-${question.question_id}`}
                                                className={`${isUnanswered && !isDisabled ? 'border-warning border-2' : ''}`}
                                            >
                                                {isUnanswered && !isDisabled && (
                                                    <div className="bg-warning bg-opacity-10 p-2 rounded mb-2">
                                                        <span className="text-warning fw-semibold fs-8">
                                                            ⚠️ This question has not been answered
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Question Header */}
                                                <div className="d-flex align-items-center gap-3 mb-3">
                                                    <div className="bg-light-primary rounded-circle p-2 d-flex align-items-center justify-content-center flex-shrink-0 mt-1">
                                                        <CircleHelp size={18} className="text-primary" />
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <p className="fw-semibold text-gray-900 mb-0 fs-5">
                                                                {question.question}
                                                            </p>
                                                            <ThemeTooltip typeTooltip="default" theme="light" text={`${isDisabled ? "Read Only Mode" : "Edit Mode"}`} Wrapper={<span className={`badge ${isDisabled ? 'bg-light-danger text-danger' : 'bg-light-success text-success'} p-2  d-flex align-items-center gap-1 flex-shrink-0 ms-2`}>
                                                                {isDisabled ? <Lock size={16} /> : <Unlock size={16} />}
                                                            </span>} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row g-2 border rounded mt-2">
                                                    <div className="col-12 col-md-6">
                                                        <div className="h-100">
                                                            {/* Rating Slider */}
                                                            <div className="p-4 d-flex flex-column h-100">
                                                              <div>
                                                                  <p className="fw-semibold text-gray-700 mb-5">
                                                                    Based on your understanding provide an appropriate score that
                                                                    considers both the above questions and the evaluators reading of
                                                                    the project.
                                                                </p>
                                                                <div className="d-flex align-items-center gap-5 mb-5">
                                                                    <div className="score-slider w-100 mt-50">
                                                                        <div>
                                                                            {/* Numbers */}
                                                                            <div className="d-flex justify-content-between mb-1">
                                                                                {marks.map((mark) => (
                                                                                    <span key={mark} className="mark-number fs-8">
                                                                                        {mark}
                                                                                    </span>
                                                                                ))}
                                                                            </div>

                                                                            {/* Slider */}
                                                                            <input
                                                                                type="range"
                                                                                min={minMaxScore.min}
                                                                                max={minMaxScore.max}
                                                                                step={0.5}
                                                                                value={displayScore}
                                                                                onChange={(e) =>
                                                                                    handleScoreChange(fieldName, question.question_id, e)
                                                                                }
                                                                                className={`custom-range-slider w-100 h-15px rounded-pill ${isDisabled ? "disabled-slider" : ""
                                                                                    }`}
                                                                                disabled={isDisabled}
                                                                                style={{
                                                                                    opacity: isDisabled ? 0.6 : 1,
                                                                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                                                                }}
                                                                            />
                                                                            {showRatingError && (
                                                                                <p className="text-danger fs-8 fw-semibold mt-2 mb-0">
                                                                                    Please rate this question before continuing.
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                </div>
                                                              </div>
                                                                <div className="d-flex justify-content-start gap-5 mt-2">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm py-2 btn-light-info border border-info"
                                                                        onClick={() => openModal(sub.pp_id)}
                                                                    >
                                                                        Reference Document
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-secondary text-gray-700 py-2 border border-gray-500"
                                                                        onClick={() => handleOpenScoreHelpModal(question.score_help)}
                                                                    >
                                                                        <HelpCircleIcon size={16} /> Guidance
                                                                    </button>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <div className="h-100">
                                                            {/* Reasoning Editor */}
                                                            <div className="p-4">
                                                                <p className="fw-semibold text-gray-700 mb-2">
                                                                    Reasoning your score by detailing how the project leverages ICT
                                                                    for business and government efficiencies.
                                                                </p>
                                                                {isDisabled ? (
                                                                    <div className="p-3 bg-white rounded-3 border min-h-200px">
                                                                        <div
                                                                            className="text-gray-500 mb-0"
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: response?.comment || '<em>No reasoning provided.</em>',
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <MyEditor
                                                                        isMiniEditor={true}
                                                                        value={fieldValue?.comment ?? ''}
                                                                        setValue={(value: string) => { handleCommentChange(fieldName, question.question_id, value); }}
                                                                        height={200}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>

                                            </div>
                                        );
                                    })}
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-gray-500">No questions available for this sub-principle.</p>
                            </div>
                        )}
                    </div>
                ),
            };
        });
    }, [calculateWeightedScore, allowEdit, getDisplayScore, openImageModal, marks, minMaxScore, likertScaleLabels, openHelpModal, markQuestionTouched, openModal, ratingErrorQuestionIds]);

    // Loading state
    if (!evaluationPrinciples || evaluationPrinciples.length === 0) {
        return (
            <div className="card p-4 shadow-sm">
                <div className="text-center py-4">
                    <p className="text-gray-500">No principles available</p>
                </div>
            </div>
        );
    }

    const isLastTab = currentTabIndex === principleTabs.length - 1;

    return (
        <Formik<FormValues>
            initialValues={initialValues}
            onSubmit={async (values) => {
                await handleSave(values);
            }}
            enableReinitialize
        >
            {({ values, setFieldValue }) => {
                return (
                    <Form>
                        <div className="principle-tabs">
                            {/* Tab Headers */}
                            <div className="d-flex gap-2 mb-3 overflow-auto pb-2" style={{ flexWrap: 'nowrap' }}>
                                {principleTabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        className={`d-flex align-items-center gap-2 px-3 py-2 border-0 rounded-3 fw-semibold fs-7 transition-all flex-shrink-0 ${selectedTab === Number(tab.id)
                                            ? 'bg-primary text-white shadow-sm'
                                            : 'bg-light text-gray-700'
                                            }`}
                                        onClick={() => handleTabChange(Number(tab.id))}
                                        style={{
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            minWidth: '140px',
                                        }}
                                    >
                                        <span className={selectedTab === Number(tab.id) ? 'bg-white p-2 rounded-circle' : 'bg-light-primary p-2 rounded-circle'}>
                                            {tab.icon}
                                        </span>
                                        <span>{tab.label}</span>
                                        {tab.progress && tab.progress.total > 0 && (
                                            <span className={`badge ${selectedTab === Number(tab.id) ? 'bg-white text-primary' : 'bg-light-success text-success'} rounded-pill ms-1`}>
                                                {tab.progress.completed}/{tab.progress.total}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Tab Content */}
                            <div className="tab-content">
                                {principleTabs.map((tab, index: number) => (
                                    <div
                                        key={tab.id}
                                        className={`tab-pane fade ${selectedTab === Number(tab.id) ? 'show active' : ''}`}
                                        style={{ display: selectedTab === Number(tab.id) ? 'block' : 'none' }}
                                    >
                                        <div className="card border-0 shadow-none">
                                            <div className="card-body p-0">
                                                {/* Principle Header */}
                                                <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
                                                    <div className="d-flex align-items-center gap-3 ms-1">
                                                        <div>
                                                            <h5 className="fw-bold mb-1">{tab.label}</h5>
                                                            <p className="text-gray-600 fs-7 mb-0">{tab.description}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Sub-Principles Accordion */}
                                                <div className="mt-4">
                                                    <CustomAccordionCards
                                                        items={generateAccordionItems(tab.subPrinciples, {
                                                            values,
                                                            setFieldValue,
                                                        })}
                                                        activeId={selectedTab === Number(tab.id) ? openSubPrincipleId : undefined}
                                                        onToggle={(id, isOpen) => {
                                                            if (selectedTab === Number(tab.id)) {
                                                                setOpenSubPrincipleId(isOpen ? Number(id) : null);
                                                            }
                                                        }}
                                                    />
                                                </div>

                                                {/* Action Buttons */}
                                                {allowEdit && (
                                                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                                        <div className="d-flex gap-2">
                                                            {index > 0 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light-primary"
                                                                    onClick={goToPreviousTab}
                                                                >
                                                                    <ChevronLeft size={16} className="me-1" /> Back
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            {!isLastTab ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        onClick={() => handleSaveAndNext(values)}
                                                                        disabled={isSaving}
                                                                    >
                                                                        <ArrowRight size={16} className="me-1" />
                                                                        Save & Next
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        onClick={async () => {
                                                                            await handleSave(values);
                                                                        }}
                                                                        disabled={isSaving}
                                                                    >
                                                                        <Save size={16} className="me-1" />
                                                                        {isSaving ? 'Saving...' : 'Save'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-success"
                                                                        onClick={handleOpenCompleteConfirmation}
                                                                        disabled={isCompleting || !readyToCompleteEvaluation}
                                                                    >
                                                                        <CheckCircle size={16} className="me-1" />
                                                                        Complete Evaluation
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <ReferenceDocumentModal
                            isOpen={isOpen}
                            onClose={closeModal}
                            projectId={projectId}
                            principleId={principleId}
                            title="Reference Documents"
                            projectName=""
                        />

                        <ScoreSliderImagesModal
                            isOpen={isImageModalOpen}
                            onClose={closeImageModal}
                            fileUrl={currentImageUrl}
                        />

                        <ScoreHelpModal
                            isOpen={isHelpModalOpen}
                            onClose={closeHelpModal}
                            scoreHelp={scoreHelp}
                            scoringKeywords={scoringKeywords}
                            likertScaleLabels={likertScaleLabels}
                        />

                        <ConfirmationModal
                            show={showCompleteConfirmation}
                            onClose={handleCloseCompleteConfirmation}
                            onConfirm={handleCompleteEvaluation}
                            modalTitle="Complete Evaluation"
                            deleteMessage="Once you complete evaluation, you will not be able to modify any data. Do you want to complete ?"
                            actionButtonText="Complete Evaluation"
                            popupLoader={isCompleting}
                            confirmClass="success"
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};
